import React, { useState } from 'react';

import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import {
  faCircleExclamation,
  faSpinnerThird,
} from '@awesome.me/kit-a6e51d954f/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Honeybadger from '@honeybadger-io/js';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert } from '../../../../../shared/components/Alert';
import { ERROR_TITLES, SUCCESS_MESSAGES } from '../../constants';
import { formatDate } from '../../utils';

const CancelButton = ({ children, isLoading, onClick }) => {
  if (isLoading) {
    return (
      <AlertDialogAction disabled>
        <FontAwesomeIcon className="animate-spin" icon={faSpinnerThird} />
        Please wait
      </AlertDialogAction>
    );
  }

  return <AlertDialogAction onClick={onClick}>{children}</AlertDialogAction>;
};

CancelButton.propTypes = {
  children: PropTypes.node.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const Dialog = ({ currentPeriodEndAt, isOpen, onCancel, onClose }) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSuccess = () => {
    onClose();
    toast.success(SUCCESS_MESSAGES.subscription.cancel);
  };

  const handleCancel = async (e) => {
    // <AlertDialogAction /> closes the dialog on click, without waiting for
    // handleCancel() to finish execution. e.preventDefault() prevents this behavior.
    e.preventDefault();

    try {
      setErrorMessage('');
      setIsLoading(true);
      await onCancel();
      handleSuccess();
    } catch (error) {
      setErrorMessage(error.message);
      Honeybadger.notify(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      onClose();
      setErrorMessage('');
    }
  };

  return (
    <AlertDialog onOpenChange={handleOpenChange} open={isOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>

          <AlertDialogDescription>
            Your subscription will stay active until {formatDate(currentPeriodEndAt)}.
            You&apos;ll keep full access until then, after which your subscription will
            end.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!isEmpty(errorMessage) && (
          <Alert
            description={errorMessage}
            icon={<FontAwesomeIcon icon={faCircleExclamation} />}
            title={ERROR_TITLES.subscription.cancel}
            variant="destructive"
          />
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Keep subscription</AlertDialogCancel>

          <CancelButton isLoading={isLoading} onClick={handleCancel}>
            Cancel
          </CancelButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

Dialog.propTypes = {
  currentPeriodEndAt: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default Dialog;
